/**
 * authApi —— 对接后端 /api/auth/* 端点（login / me / logout / change-password）。
 *
 * 复用 services/api.ts 的共享 axios 实例（HttpOnly Cookie 会话与 401/403 拦截）。
 * 统一响应信封：成功 { success:true, data }，失败 { success:false, error, code }。
 * 这里只解包 data 给上层（AuthContext / LoginPage），错误以 axios 异常向上抛。
 */
import api from './api';

/** 身份来源：本地密码库或企业 SAML 身份提供方。 */
export type AuthSource = 'LOCAL' | 'SAML';

/** 已认证用户（与后端 AuthenticatedUser 对齐）。 */
export interface AuthenticatedUser {
  userId: number;
  username: string;
  displayName: string;
  /** role_code 列表。 */
  roles: string[];
  source: AuthSource;
}

/** 当前用户绑定的员工（员工自助视角用）。无绑定为 null。 */
export interface LinkedEmployee {
  employeeId: number;
  employeeCode: string;
  employeeName: string;
}

/** 当前用户可访问的 Site 引用（与后端 SiteReference 对齐）。 */
export interface SiteReference {
  siteId: number;
  publicId: string;
  siteCode: string;
  siteName: string;
  siteStatus: 'PROVISIONING' | 'INACTIVE' | 'ACTIVE' | 'SUSPENDED' | 'RETIRED';
  timezone: string;
  dataRegion: string | null;
  policyVersion: number;
  orgGraphVersion: number;
  dataPlacementVersion: number;
}

/** 当前用户在一个 Site 下的有效成员关系。 */
export interface SiteMembership {
  membershipId: number;
  membershipStatus: 'ACTIVE' | 'SUSPENDED' | 'REVOKED';
  isDefault: boolean;
  authzVersion: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  site: SiteReference;
}

/** 一个有效权限对应的具体 Site Grant 来源。相同权限可能有多条来源。 */
export interface EffectivePermissionGrant {
  assignmentId: number;
  roleId: number;
  roleCode: string;
  roleVersion: number;
  permissionCode: string;
  scopeType: 'SITE' | 'ORG_SUBTREE' | 'SELF';
  siteId: number;
  scopeOrgUnitId: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
}

/** POST /api/auth/login 的 data。 */
export interface LoginResult {
  user: AuthenticatedUser;
  mustChangePassword: boolean;
}

/** GET /api/auth/me 的 data。 */
export interface MeResult {
  user: AuthenticatedUser;
  /** 该用户的权限码列表（permission_code）。 */
  permissions: string[];
  /** 当前 Site 有效权限的逐 Grant 来源，不折叠重复权限码。 */
  permissionGrants: EffectivePermissionGrant[];
  /** 与 Site membership 无关的平台控制面权限码列表。 */
  platformPermissions: string[];
  /** 当前用户绑定的员工（员工自助）。无绑定为 null。 */
  linkedEmployee: LinkedEmployee | null;
  /** 当前用户全部有效 Site membership；门禁阶段只应包含 MFG8。 */
  memberships: SiteMembership[];
  /** 本次权限快照对应的 Site。 */
  currentSite: SiteReference | null;
  /** 当前 Site membership 的授权版本。 */
  authzVersion: number | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  error?: string;
  code?: string;
}

export const authApi = {
  /** 登录并建立 HttpOnly Cookie 会话。失败时由调用方捕获安全错误信封。 */
  login: (username: string, password: string) =>
    api
      .post<Envelope<LoginResult>>('/auth/login', { username, password })
      .then((res) => res.data.data),

  /** 查询后端是否已完整配置 SAML；登录协议与密钥不进入前端构建变量。 */
  samlStatus: () =>
    api
      .get<Envelope<{ configured: boolean }>>('/auth/saml/status')
      .then((res) => res.data.data),

  /** One-release bridge: promote a verified legacy bearer into the HttpOnly cookie. */
  upgradeLegacySession: (token: string) =>
    api
      .post<Envelope<{ upgraded: boolean }>>(
        '/auth/session-upgrade',
        undefined,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => res.data.data),

  /** 用当前 Cookie 会话水合身份 + 权限。无效会话触发 401。 */
  me: (sitePublicId?: string | null) =>
    api
      .get<Envelope<MeResult>>(
        sitePublicId
          ? `/sites/${encodeURIComponent(sitePublicId)}/session`
          : '/auth/me',
      )
      .then((res) => res.data.data),

  /** 登出并让后端清除 HttpOnly Cookie。 */
  logout: () =>
    api.post<Envelope<{ loggedOut: boolean }>>('/auth/logout').then((res) => res.data.data),

  /** 修改当前登录用户密码。 */
  changePassword: (oldPassword: string, newPassword: string) =>
    api
      .post<Envelope<{ changed: boolean }>>('/auth/change-password', { oldPassword, newPassword })
      .then((res) => res.data.data),
};
